import React, { useState, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileCsv,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";

const TrafficDashboardDataGrid = ({ data }) => {
  const formatDate = (dateString) => {
    if (!dateString) return "Invalid Date";
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const abbreviate = (str, maxLength = 20) => {
    if (str.length > maxLength) {
      return str.slice(0, maxLength) + "...";
    }
    return str;
  };

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });

  const sortedData = useMemo(() => {
    let sortableItems = Array.isArray(data) ? [...data] : [];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        const aValue = sortConfig.key
          .split(".")
          .reduce(
            (acc, key) => (acc && acc[key] !== undefined ? acc[key] : null),
            a
          );
        const bValue = sortConfig.key
          .split(".")
          .reduce(
            (acc, key) => (acc && acc[key] !== undefined ? acc[key] : null),
            b
          );
        if (aValue < bValue) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [data, sortConfig]);

  const exportToCSV = () => {
    let csvContent = "data:text/csv;charset=utf-8,";
    csvContent +=
      "Reference,Entry Point,License,Pedimento,Operation Type,Regimen,Pedimento Date,Customer Name,Paid Date,Crossing Date\n";
    data.forEach((item) => {
      const reference = item?.header?.reference || "";
      const entryPoint = item?.header?.pedimento?.pedimento_entry_point || "";
      const license = item?.header?.pedimento?.pedimento_license || "";
      const pedimentoNumber = item?.header?.pedimento?.pedimento_number || "";
      const operationType =
        item?.header?.operation_type === "1" ? "Import" : "Export";
      const regime = item?.header?.detail?.traffic_data?.regime || "";
      const pedimentoDate = formatDate(
        item?.header?.detail?.dates_data?.date_init
      );
      const customerName = item?.header?.customer_name || "";
      const paidDate = formatDate(
        item?.header?.detail?.dates_data?.date_pedimento_paid
      );
      const crossingDate = formatDate(
        item?.header?.detail?.dates_data?.date_crossing
      );

      csvContent += `${reference},${entryPoint},${license},${pedimentoNumber},${operationType},${regime},${pedimentoDate},${customerName},${paidDate},${crossingDate}\n`;
    });
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "data_grid_export.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const onSortChange = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    } else {
      direction = "ascending";
    }
    setSortConfig({ key, direction });
  };

  if (!Array.isArray(data)) {
    return <div>Data is not available.</div>;
  }

  const headers = [
    { title: "Reference", key: "header.reference", width: "150px" },
    {
      title: "Entry Point",
      key: "header.pedimento.pedimento_entry_point",
      width: "100px",
    },
    {
      title: "License",
      key: "header.pedimento.pedimento_license",
      width: "100px",
    },
    {
      title: "Pedimento",
      key: "header.pedimento.pedimento_number",
      width: "150px",
    },
    { title: "Operation Type", key: "header.operation_type", width: "120px" },
    {
      title: "Regimen",
      key: "header.detail.traffic_data.regime",
      width: "100px",
    },
    {
      title: "Pedimento Date",
      key: "header.detail.dates_data.date_init",
      width: "150px",
    },
    { title: "Customer Name", key: "header.customer_name", width: "200px" },
    {
      title: "Paid Date",
      key: "header.detail.dates_data.date_pedimento_paid",
      width: "150px",
    },
    {
      title: "Crossing Date",
      key: "header.detail.dates_data.date_crossing",
      width: "150px",
    },
  ];

  return (
    <div className="col-span-2 bg-white shadow rounded-lg p-4 overflow-hidden">
      <div className="mb-2 flex justify-between items-center">
        <h2 className="text-lg font-bold text-gray-800">Data Grid</h2>
        <button onClick={exportToCSV} title="Export to CSV">
          <FontAwesomeIcon icon={faFileCsv} size="lg" />
        </button>
      </div>
      <div className="overflow-auto max-h-96">
        <div className="overflow-x-auto">
          <div className="flex mb-1 rounded-t-lg bg-gray-200 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider shadow min-w-max">
            {headers.map((header) => (
              <div
                key={header.key}
                className="px-2 py-1 cursor-pointer text-center"
                style={{ flex: `0 0 ${header.width}` }}
                onClick={() => onSortChange(header.key)}
              >
                {header.title}
                {sortConfig.key === header.key && (
                  <FontAwesomeIcon
                    icon={
                      sortConfig.direction === "ascending"
                        ? faSortUp
                        : faSortDown
                    }
                  />
                )}
              </div>
            ))}
          </div>
          {sortedData.map((item, idx) => (
            <div
              key={idx}
              className={`flex text-center text-sm min-w-max ${
                idx % 2 === 0 ? "bg-gray-100" : "bg-gray-200"
              }`}
            >
              {headers.map((header) => (
                <div
                  key={header.key}
                  className="px-2 py-1 text-center"
                  style={{ flex: `0 0 ${header.width}` }}
                  title={
                    header.key.includes("date")
                      ? formatDate(
                          header.key
                            .split(".")
                            .reduce(
                              (acc, key) =>
                                acc && acc[key] !== undefined ? acc[key] : null,
                              item
                            )
                        )
                      : header.key
                          .split(".")
                          .reduce(
                            (acc, key) =>
                              acc && acc[key] !== undefined ? acc[key] : null,
                            item
                          )
                  }
                >
                  {header.key.includes("date")
                    ? formatDate(
                        header.key
                          .split(".")
                          .reduce(
                            (acc, key) =>
                              acc && acc[key] !== undefined ? acc[key] : null,
                            item
                          )
                      )
                    : header.key === "header.operation_type"
                    ? item.header.operation_type === "1"
                      ? "Import"
                      : "Export"
                    : abbreviate(
                        header.key
                          .split(".")
                          .reduce(
                            (acc, key) =>
                              acc && acc[key] !== undefined ? acc[key] : null,
                            item
                          ),
                        15
                      )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TrafficDashboardDataGrid;
